import { 
  Logger, 
  LogLevel, 
  LogContext, 
  LogEntry,
  LoggerConfig,
  LOG_LEVEL_PRIORITY,
  LOG_COLORS
} from './logger.types';
import { JSONValue } from '@/types/common';

/**
 * Application-wide logger utility
 * 
 * Features:
 * - Environment-aware logging (dev vs prod)
 * - Log level filtering
 * - Structured context logging
 * - Performance timing
 * - Colored output in development
 * - Backwards compatible with existing LoggerOptions
 */

// Legacy interface for backwards compatibility
export interface LoggerOptions {
  module?: string;
  user?: string;
  data?: JSONValue;
  redact?: boolean;
  errorStack?: string;
  url?: string;
  count?: number;
  rawDataKeys?: string[];
  hasCompany?: boolean;
  parseErrorMessage?: string;
  errorMessage?: string;
  analysisComplete?: boolean;
  analysisDataId?: string;
  companyName?: string;
  valuePropositionLength?: number;
  keyMessagesCount?: number;
  personasCount?: number;
  id?: string;
  crawlId?: string;
  readyFlag?: string;
  error?: Error | string;
  componentStack?: string;
}

class AppLogger implements Logger {
  private config: LoggerConfig;
  private timers = new Map<string, number>();

  constructor() {
    // Check if we're in development mode
    let isDevelopment = false;
    
    // Handle different environments
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      // Vite environment
      isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';
    } else if (typeof process !== 'undefined' && process.env) {
      // Node.js environment
      isDevelopment = process.env.NODE_ENV !== 'production';
    }
    
    this.config = {
      isDevelopment,
      logLevel: isDevelopment ? 'debug' : 'info',
      enableTimestamps: true,
      enableColors: isDevelopment && typeof window !== 'undefined'
    };
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[this.config.logLevel];
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
    
    if (this.config.isDevelopment && this.config.enableColors) {
      const color = LOG_COLORS[level];
      const reset = LOG_COLORS.reset;
      return `${color}${prefix}${reset} ${message}`;
    }
    
    return `${prefix} ${message}`;
  }

  private formatContext(context: LogContext): string {
    // Filter out undefined values and errors (handled separately)
    const cleanContext = Object.entries(context).reduce((acc, [key, value]) => {
      if (value !== undefined && key !== 'error') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, JSONValue>);

    if (Object.keys(cleanContext).length === 0) {
      return '';
    }

    return JSON.stringify(cleanContext, null, this.config.isDevelopment ? 2 : 0);
  }

  private log(level: LogLevel, message: string, context?: LogContext, error?: Error): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const formattedMessage = this.formatMessage(level, message, context);
    const contextStr = context ? this.formatContext(context) : '';
    
    // Choose the appropriate console method
    const consoleMethod = level === 'error' ? console.error :
                         level === 'warn' ? console.warn :
                         level === 'debug' ? console.debug :
                         console.log;

    // Log the message
    if (contextStr) {
      consoleMethod(formattedMessage, '\n', contextStr);
    } else {
      consoleMethod(formattedMessage);
    }

    // Log error stack trace if present
    if (error) {
      if (this.config.isDevelopment) {
        consoleMethod('Stack trace:', error.stack);
      } else {
        // In production, only log error message
        consoleMethod('Error:', error.message);
      }
    }

    // In production, you might want to send logs to a service
    if (!this.config.isDevelopment && level === 'error') {
      // TODO: Send to error tracking service (e.g., Sentry)
      // this.sendToErrorTracking({ message, context, error });
    }
  }

  debug(message: string, context?: LogContext): void {
    this.log('debug', message, context);
  }

  info(message: string, context?: LogContext): void {
    this.log('info', message, context);
  }

  warn(message: string, context?: LogContext): void {
    this.log('warn', message, context);
  }

  error(message: string, error?: Error | string, context?: LogContext): void {
    if (typeof error === 'string') {
      this.log('error', message, { ...context, errorMessage: error });
    } else {
      this.log('error', message, { ...context, error: error?.message }, error);
    }
  }

  time(label: string): void {
    this.timers.set(label, performance.now());
    this.debug(`Timer started: ${label}`);
  }

  timeEnd(label: string): number | undefined {
    const startTime = this.timers.get(label);
    if (!startTime) {
      this.warn(`Timer '${label}' does not exist`);
      return undefined;
    }

    const duration = performance.now() - startTime;
    this.timers.delete(label);
    
    this.info(`Timer ended: ${label}`, { 
      duration: Math.round(duration * 100) / 100,
      durationUnit: 'ms'
    });
    
    return duration;
  }

  setLogLevel(level: LogLevel): void {
    this.config.logLevel = level;
    this.info(`Log level changed to: ${level}`);
  }

  getLogLevel(): LogLevel {
    return this.config.logLevel;
  }
}

// Create singleton instance
export const logger = new AppLogger();

// Export for use in edge functions where import.meta might not be available
export function createLogger(isDevelopment: boolean = false): Logger {
  const customLogger = new AppLogger();
  if (isDevelopment) {
    customLogger.setLogLevel('debug');
  }
  return customLogger;
}

// Legacy functions for backwards compatibility
const debug = (message: string, data?: JSONValue, options?: LoggerOptions) => {
  const context: LogContext = { ...options };
  if (data !== undefined) context.data = data;
  logger.debug(message, context);
};

const info = (message: string, data?: JSONValue, options?: LoggerOptions) => {
  const context: LogContext = { ...options };
  if (data !== undefined) context.data = data;
  logger.info(message, context);
};

const warn = (message: string, data?: JSONValue, options?: LoggerOptions) => {
  const context: LogContext = { ...options };
  if (data !== undefined) context.data = data;
  logger.warn(message, context);
};

const error = (message: string, err?: Error | JSONValue, options?: LoggerOptions) => {
  const context: LogContext = { ...options };
  if (err instanceof Error) {
    logger.error(message, err, context);
  } else if (err !== undefined) {
    context.data = err;
    logger.error(message, undefined, context);
  } else {
    logger.error(message, undefined, context);
  }
};

// Export default for backwards compatibility
export default {
  debug,
  info,
  warn,
  error,
};